import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    Container,
    IconButton,
    Paper,
    Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import { Alert } from '@features/alert';
import { useOrder } from '../api/useOrder';
import { TypographyOrderStatus } from '@features/typography-colored-status-order';
import { ORDER_STATUSES } from '@shared/api/constants';
import { LoadingPage } from '@widgets/loading-page';

export const OrderViewPage = () => {
    const { id: orderId } = useParams();
    const {
        alertRef,
        order,
        isLoading,
        formatDate,
        uniquePacks,
        removePackFromScanned,
        addScannedPack,
        handleCreateConsignmentNote
    } = useOrder(orderId);


    const [scanValue, setScanValue] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        const interval = setInterval(() => {
            const activeTag = document.activeElement?.tagName;
            if (
                document.activeElement !== inputRef.current &&
                !['BUTTON', 'A'].includes(activeTag)
            ) {
                inputRef.current?.focus();
            }
        }, 300);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const handleKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (scanValue.trim()) {
                await addScannedPack(scanValue.trim());
            }
            setScanValue('');
        }
    };

    // Сравниваем, совпадают ли уже полностью
    // (например, чтобы кнопку «Create consignment note» разблокировать)
    const [isMatch, setIsMatch] = useState(false);

    // Функция для формирования ключа
    const getPackKey = useCallback((pack) => {
        return `${pack.weight}_${pack.grind}_${pack.roastType._id}_${pack.country._id}_${pack.producerName}`;
    }, []);

    // Сравнительная логика
    const comparePacks = useCallback((packsA, packsB) => {
        if (!packsA || !packsB || packsA.length !== packsB.length) return false;

        // Сортируем оба массива
        const sortedA = [...packsA].sort((a, b) => getPackKey(a).localeCompare(getPackKey(b)));
        const sortedB = [...packsB].sort((a, b) => getPackKey(a).localeCompare(getPackKey(b)));

        for (let i = 0; i < sortedA.length; i++) {
            const keyA = getPackKey(sortedA[i]);
            const keyB = getPackKey(sortedB[i]);
            if (keyA !== keyB || sortedA[i].count !== sortedB[i].count) {
                return false;
            }
        }
        return true;
    }, [getPackKey]);

    useEffect(() => {
        if (order?.packs && uniquePacks) {
            setIsMatch(comparePacks(order.packs, uniquePacks));
        }
    }, [order, uniquePacks, comparePacks]);

    if (isLoading) return <LoadingPage/>;


    return (
        <>
            <Container maxWidth="sm" sx={{ padding: 2, height: '80vh' }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{
                        position: 'absolute',
                        opacity: 0,
                        pointerEvents: 'auto',
                        zIndex: -1,
                        right: '200px'
                    }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 2 }}>
                    <Button variant="contained" color="primary" component={Link} to={`../orders`}>
                        back
                    </Button>
                </Box>

                <Paper sx={{ flexGrow: 1, overflow: 'auto', p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                            <Box display="flex" justifyContent="space-between"
                                 sx={{ borderBottom: '1px solid #ccc', pb: 2 }}>
                                <Typography variant="h5">order information</Typography>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 9 }}>
                            <Typography variant="body1">
                                <strong>id:</strong> {order?.uuid}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                            <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'end' }}>
                                {order?.created_at && formatDate(order.created_at)}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TypographyOrderStatus status={order?.status} variant="body1"/>
                        </Grid>

                        {/* packs from order */}
                        {order?.packs?.length > 0 && (
                            <>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        packs information
                                    </Typography>
                                </Grid>
                                {order.packs.map((pack) => (
                                    <Grid
                                        size={{ xs: 12 }}
                                        container
                                        key={getPackKey(pack)}
                                        spacing={1}
                                        sx={{ borderBottom: '1px solid #e0e0e0', pl: 2, pb: 2, mb: 1 }}
                                    >
                                        <Box
                                            sx={{
                                                width: { xs: 32, sm: 36, md: 40 },
                                                height: { xs: 32, sm: 36, md: 40 },
                                                borderRadius: '50%',
                                                backgroundColor: 'grey.300',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 'bold',
                                                fontSize: { xs: 14, sm: 16 },
                                            }}
                                        >
                                            x{pack.count}
                                        </Box>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="body2">
                                                {pack.country.name} · {pack.producerName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {pack.weight} kg | {pack.roastType.name.en}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                            <Typography variant="body2">
                                                <strong>grind:</strong> {pack.grind}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                ))}
                            </>
                        )}

                        {/* scanned packs */}
                        <Grid container size={12}>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                scanned packs
                            </Typography>

                            {uniquePacks.map((pack) => {
                                // Нужно узнать, сколько требовалось
                                const orderEquivalent = order?.packs?.find((p) => getPackKey(p) === getPackKey(pack));
                                const neededCount = orderEquivalent?.count || 0;
                                const isFullyScanned = pack.count >= neededCount && neededCount > 0;

                                return (
                                    <Grid size={12} mb={1} key={getPackKey(pack)}>
                                        <Card sx={{ position: 'relative', p: 2, width: '100%' }}>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    display: order?.status === ORDER_STATUSES.RECEIVED ? 'flex' : 'none'
                                                }}
                                                onClick={() => removePackFromScanned(pack)}
                                            >
                                                <Close fontSize="small"/>
                                            </IconButton>

                                            <Box display="flex" alignItems="center" gap={2}>
                                                {/* Подсвечиваем кружок зелёным, если достигли нужного количества */}
                                                <Box
                                                    sx={{
                                                        minWidth: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        backgroundColor: isFullyScanned ? 'lightgreen' : 'grey.300',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    x{pack.count}
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle1">
                                                        {pack.country.name} · {pack.producerName}
                                                    </Typography>
                                                    <Typography variant="body1" color="text.secondary">
                                                        {pack.weight} kg | {pack.roastType.name.en}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {/* Кнопка неактивна, пока не совпадёт полностью */}
                        <Grid size={{ xs: 12 }}>
                            {order?.status === ORDER_STATUSES.RECEIVED && (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="large"
                                    disabled={!isMatch}
                                    onClick={handleCreateConsignmentNote}
                                >
                                    create consignment note
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
            <Alert ref={alertRef}/>
        </>
    );
};
